import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { useEffect } from "react";
import type { User } from "@shared/schema";

interface AuthStatus {
  isAuthenticated: boolean;
  user: Pick<User, "id" | "email" | "firstName" | "lastName" | "role"> & {
    is_superadmin?: boolean;
  } | null;
  entraAvailable: boolean;
  orgSsoAvailable?: boolean;
}

export function useAuth() {
  const { data: authStatus, isLoading, error, refetch: refetchAuthStatus } = useQuery<AuthStatus | null>({
    queryKey: ["/api/auth/status"],
    queryFn: getQueryFn<AuthStatus | null>({ on401: "returnNull" }),
    retry: false,
    refetchOnWindowFocus: false, // Reduced frequency - only on mount and interval
    refetchOnMount: true, // Always check on mount
    staleTime: 60 * 1000, // Consider data fresh for 60 seconds (increased from 0)
    refetchInterval: 60000, // Check auth status every 60 seconds (reduced from 30s)
  });

  const { data: userData, isLoading: userLoading, refetch: refetchUserData } = useQuery<(User & { effectiveSessionTimeout?: number }) | null>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn<User | null>({ on401: "returnNull" }),
    retry: false,
    enabled: authStatus?.isAuthenticated === true,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes (increased from 30s)
    refetchOnWindowFocus: false, // Reduced frequency
  });

  // Calculate final loading state properly - don't stay in loading if auth is false
  const finalLoading = isLoading || (authStatus?.isAuthenticated === true && userLoading);

  // SECURITY: Simple auth validation without aggressive reloads
  useEffect(() => {
    // If we have an auth status response but it's inconsistent, refetch
    if (authStatus?.isAuthenticated && !userData && !userLoading) {
      refetchUserData();
    }
  }, [authStatus, userData, userLoading, refetchUserData]);

  return {
    user: userData || null,
    isLoading: finalLoading,
    isAuthenticated: authStatus?.isAuthenticated || false,
    entraAvailable: authStatus?.entraAvailable || false,
    error,
    refetchAuth: () => {
      refetchAuthStatus();
      refetchUserData();
    }
  };
}
